/**
 * Modern Sound Manager Service for Lobby2.0
 * 
 * Handles all audio functionality with proper state management integration,
 * performance optimization, and browser compatibility.
 * 
 * Features:
 * - Howler.js integration with sound sprites
 * - Zustand store integration for mute states
 * - External communication support
 * - Mobile autoplay compliance
 * - Performance optimizations
 * - Error handling and fallbacks
 */

import type { HowlOptions } from 'howler';
import { Howl } from 'howler';
import { SOUND_SPRITES } from '@repo/common';
import { useAppStore } from 'store/app/appStore';
import { features } from '../../config/features';

export type SoundId = keyof typeof SOUND_SPRITES;

interface SoundManagerConfig {
  volume: number;
  html5: boolean;
  preload: boolean;
}

interface InitializationError {
  message?: string;
}

interface SoundManagerState {
  isInitialized: boolean;
  isLoading: boolean;
  hasError: boolean;
  errorMessage?: string;
  isUnlocked: boolean;
  soundsPlayed: number;
}

type StateChangeListener = (state: SoundManagerState) => void;

class SoundManagerService {
  private static instance: SoundManagerService;
  private howl: Howl | null = null;
  private config: SoundManagerConfig;
  private state: SoundManagerState;
  private unsubscribeStore?: () => void;
  private initPromise: Promise<void> | null = null;
  private lastSoundTimes: Map<SoundId, number> = new Map();
  private readonly SOUND_THROTTLE_MS = 100; // Minimum time between same sound plays
  private stateChangeListeners: Set<StateChangeListener> = new Set();

  private constructor() {
    this.config = {
      volume: 1, // Match old lobby: 100% volume
      html5: false, // Use Web Audio API for sound effects, not HTML5 audio
      preload: true,
    };

    this.state = {
      isInitialized: false,
      isLoading: false,
      hasError: false,
      isUnlocked: false,
      soundsPlayed: 0,
    };

    this.setupStoreSubscription();
    this.setupEventListeners();

  }

  /**
   * Subscribe to state changes
   */
  public subscribeToStateChanges(listener: StateChangeListener): () => void {
    this.stateChangeListeners.add(listener);
    // Immediately notify with current state
    listener(this.getState());
    
    return () => {
      this.stateChangeListeners.delete(listener);
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyStateChange(): void {
    const currentState = this.getState();
    this.stateChangeListeners.forEach(listener => {
      listener(currentState);
    });
  }

  /**
   * Singleton pattern to ensure only one instance exists
   */
  public static getInstance(): SoundManagerService {
    if (!SoundManagerService.instance) {
      SoundManagerService.instance = new SoundManagerService();
    }
    return SoundManagerService.instance;
  }

  /**
   * Initialize the sound manager with assets configuration
   */
  public async initialize(assetsHost: string, assetsHash: string): Promise<void> {
    // Return existing promise if already initializing
    if (this.initPromise) {
      return this.initPromise;
    }

    // Return immediately if already initialized
    if (this.state.isInitialized) {
      return Promise.resolve();
    }

    this.initPromise = this.performInitialization(assetsHost, assetsHash);
    return this.initPromise;
  }

  private async performInitialization(assetsHost: string, assetsHash: string): Promise<void> {
    if (!features.enableSoundEffects) {
      return;
    }

    try {
      this.state.isLoading = true;
      this.state.hasError = false;
      this.notifyStateChange();

      // Apply mobile optimizations before initialization
      this.optimizeForMobile();

      // Construct the sound file URL
      const soundUrl = this.buildSoundUrl(assetsHost, assetsHash);
      

      // Create Howl instance
      await this.createHowlInstance(soundUrl);

      this.state.isInitialized = true;
      this.state.isLoading = false;
      this.notifyStateChange();


    } catch (error) {
      this.handleInitializationError(error as InitializationError);
      // Don't throw error - sound loading should not block the app
      console.warn('[SoundManager] Sound loading failed, continuing without sound effects:', error);
    }
  }

  private buildSoundUrl(assetsHost: string, assetsHash: string): string {
    // Remove trailing slash from assetsHost if present
    const baseUrl = assetsHost.replace(/\/$/, '');
    
    // Include hash in URL path like other assets, and as query parameter
    // Only include hash in path if it's not empty
    const url = assetsHash && assetsHash.trim() !== ''
      ? `${baseUrl}/${assetsHash}/assets/portal/sounds/portalSounds.mp3?v=${assetsHash}`
      : `${baseUrl}/assets/portal/sounds/portalSounds.mp3`;
    
    
    return url;
  }

  private createHowlInstance(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const howlOptions: HowlOptions = {
        src: [url],
        sprite: SOUND_SPRITES,
        volume: this.config.volume,
        html5: this.config.html5,
        preload: this.config.preload,
        
        // Prevent media session registration on mobile
        autoplay: false,
        loop: false,
        
        onload: () => {
          resolve();
        },

        onloaderror: (id, error) => {
          console.error('[SoundManager] Audio load error:', { id, error });
          reject(new Error(`Failed to load audio: ${error}`));
        },

        onunlock: () => {
          this.state.isUnlocked = true;
          this.notifyStateChange();
        },

        onplayerror: (id, error) => {
          console.error('[SoundManager] Audio play error:', { id, error });
          // Don't attempt automatic replay to avoid error loops
          // The audio context will be unlocked on next user interaction
        }
      };

      this.howl = new Howl(howlOptions);
    });
  }

  /**
   * Play a sound by ID with proper mute checking and error handling
   * Matches old lobby behavior: stop other sounds except selectGameSound
   */
  public playSound(soundId: SoundId): void {
    // Early returns for disabled states
    if (!features.enableSoundEffects) {
      return;
    }

    if (!this.state.isInitialized || !this.howl) {
      return;
    }

    if (this.isSoundMuted()) {
      return;
    }

    // Throttle rapid sound triggers for performance (except selectGameSound which should always play)
    if (soundId !== 'selectGameSound') {
      const now = Date.now();
      const lastPlayed = this.lastSoundTimes.get(soundId) || 0;
      if (now - lastPlayed < this.SOUND_THROTTLE_MS) {
        return;
      }
      this.lastSoundTimes.set(soundId, now);
    }

    try {
      // Check if the sound ID exists in sprites
      if (!SOUND_SPRITES[soundId]) {
        console.error('[SoundManager] Unknown sound ID:', soundId);
        return;
      }

      // Match old lobby behavior: stop other sounds before playing new one, except selectGameSound
      const shouldStop = soundId !== 'selectGameSound';
      if (shouldStop && this.howl.playing()) {
        this.howl.stop();
      }

      // Play the sound
      const playId = this.howl.play(soundId);
      
      if (playId) {
        this.state.soundsPlayed++;
        // Don't notify for every sound played to avoid performance issues
      } else {
        console.warn('[SoundManager] Failed to get play ID for sound:', soundId);
      }

    } catch (error) {
      console.error('[SoundManager] Error playing sound:', soundId, error);
    }
  }

  /**
   * Play hover sound (desktop only)
   */
  public playHoverSound(): void {
    // Only play hover sounds on desktop to avoid mobile performance issues
    if (this.isMobileDevice()) {
      return;
    }

    this.playSound('hoverSound');
  }

  /**
   * Play game selection sound
   */
  public playSelectGameSound(): void {
    this.playSound('selectGameSound');
  }

  /**
   * Play tab/section selection sound
   */
  public playSelectTabSound(): void {
    this.playSound('selectTabSound');
  }

  /**
   * Play exit application sound
   */
  public playExitAppSound(): void {
    this.playSound('exitAppSound');
  }

  /**
   * Get the duration of a specific sound for timing purposes
   */
  public getSoundDuration(soundId: SoundId): number {
    const sprite = SOUND_SPRITES[soundId];
    if (sprite && sprite.length > 1) {
      return sprite[1]; // Duration is the second element in the sprite array
    }
    return 0;
  }

  /**
   * Set the global volume for all sounds
   */
  public setVolume(volume: number): void {
    this.config.volume = Math.max(0, Math.min(1, volume)); // Clamp between 0 and 1
    
    if (this.howl) {
      this.howl.volume(this.config.volume);
    }

  }

  /**
   * Get current volume
   */
  public getVolume(): number {
    return this.config.volume;
  }

  /**
   * Get current state of the sound manager
   */
  public getState(): Readonly<SoundManagerState> {
    return { ...this.state };
  }

  /**
   * Check if sound is currently muted
   */
  private isSoundMuted(): boolean {
    const settings = useAppStore.getState().settings;
    return settings.mute;
  }

  /**
   * Check if device is mobile
   */
  private isMobileDevice(): boolean {
    const settings = useAppStore.getState().settings;
    return settings.mode === 'mobile';
  }

  /**
   * Check if device has limited resources (for performance optimization)
   */
  private isLowEndDevice(): boolean {
    // Check for indicators of low-end devices
    const navigator = window.navigator;
    
    // Check memory constraints (if available)
    if ('deviceMemory' in navigator && navigator.deviceMemory as number <= 4) {
      return true;
    }
    
    // Check for specific low-end indicators
    const userAgent = navigator.userAgent.toLowerCase();
    const isOldAndroid = /android [1-4]\./.test(userAgent);
    const isOldIOS = /os [1-9]_/.test(userAgent);
    
    return isOldAndroid || isOldIOS;
  }

  /**
   * Optimize configuration for mobile devices
   * Keep Web Audio API for sound effects to avoid media session registration
   */
  private optimizeForMobile(): void {
    if (this.isMobileDevice() || this.isLowEndDevice()) {
      // Match old lobby: keep volume at 1.0 (don't reduce for mobile)
      // Keep Web Audio API (html5: false) to avoid iOS media controls showing
      // Web Audio API is better for sound effects anyway
      
    }
  }

  /**
   * Setup subscription to store changes for mute state
   */
  private setupStoreSubscription(): void {
    this.unsubscribeStore = useAppStore.subscribe(
      (state) => {
        const currentMute = state.settings.mute;
        
        // Optionally stop all currently playing sounds when muted
        if (currentMute && this.howl) {
          this.howl.stop();
        }
      }
    );
  }

  /**
   * Setup event listeners for user interactions
   */
  private setupEventListeners(): void {
    // Listen for first user interaction to unlock audio context
    const unlockAudioContext = () => {
      if (this.howl && !this.state.isUnlocked) {
        if (this.isSoundMuted()) return; // skip first sound if muted.

        try {
          // Try to unlock the audio context with a valid sound ID
          const soundIds = Object.keys(SOUND_SPRITES) as SoundId[];
          if (soundIds.length > 0) {
            const playId = this.howl.play(soundIds[0]); // Use first available sound
            if (playId) {
              this.howl.stop(playId); // Stop it immediately
            }
          }
          
        } catch (error) {
        }
      }
    };

    // Add event listeners for various user interactions
    const events = ['touchstart', 'touchend', 'mousedown', 'keydown'];
    const eventOptions = { once: true, passive: true };

    events.forEach(event => {
      document.addEventListener(event, unlockAudioContext, eventOptions);
    });
  }

  /**
   * Handle initialization errors
   */
  private handleInitializationError(error: InitializationError): void {
    this.state.hasError = true;
    this.state.isLoading = false;
    this.state.errorMessage = error?.message || 'Unknown initialization error';
    this.notifyStateChange();
    
    console.error('[SoundManager] Initialization failed:', error);
    
    // Don't send error to external communication - sound errors should not block the app
    // Sound functionality is optional and the lobby should work without it
  }

  /**
   * Cleanup method for when the service is no longer needed
   */
  public destroy(): void {
    if (this.howl) {
      this.howl.unload();
      this.howl = null;
    }

    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = undefined;
    }

    // Clear throttling map
    this.lastSoundTimes.clear();

    // Clear listeners
    this.stateChangeListeners.clear();

    this.state.isInitialized = false;
    this.initPromise = null;

  }

  /**
   * Reset the service state (useful for testing)
   */
  public reset(): void {
    this.destroy();
    this.state = {
      isInitialized: false,
      isLoading: false,
      hasError: false,
      isUnlocked: false,
      soundsPlayed: 0,
    };
    // lastSoundTimes is already cleared in destroy()
    this.notifyStateChange();
  }
}

// Export singleton instance
export const soundManager = SoundManagerService.getInstance();

// Export the class for testing purposes
// fallow-ignore-next-line unused-export
export { SoundManagerService };

// Export types
export type { SoundManagerConfig, SoundManagerState, StateChangeListener };
